'use client';

import { ConvexBetterAuthProvider } from '@convex-dev/better-auth/react';
import { ConvexQueryClient } from '@convex-dev/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConvexReactClient } from 'convex/react';
import { ConvexQueryCacheProvider } from 'convex-helpers/react/cache';
import { ReactNode, useEffect } from 'react';
import { authClient } from '../lib/better-auth-client-actions';
import { createConvexQueryAuthLifecycle } from '../lib/convex-query-auth-lifecycle';

const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL!, {
  verbose: process.env.NEXT_PUBLIC_CONVEX_VERBOSE === 'true',
  // No "Reload site?" beforeunload dialog for in-flight mutations. Surfaces
  // like Work Chats fire constant background mutations (markRead debounce,
  // presence heartbeats), so the warning triggered on ~every reload; losing
  // one of those on unload is harmless, and real drafts are persisted
  // separately.
  unsavedChangesWarning: false,
});
const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
    },
  },
});
convexQueryClient.connect(queryClient);
const updateConvexQueryIdentity = createConvexQueryAuthLifecycle(queryClient);

function ConvexQueryAuthLifecycle() {
  const { data: session, isPending } = authClient.useSession();
  const userId = session?.user.id ?? null;

  useEffect(() => {
    if (isPending) return;
    void updateConvexQueryIdentity(userId);
  }, [isPending, userId]);

  return null;
}

export function ConvexClientProvider({
  children,
  initialToken,
}: {
  children: ReactNode;
  initialToken?: string | null;
}) {
  return (
    <ConvexBetterAuthProvider
      client={convex}
      authClient={authClient}
      initialToken={initialToken ?? undefined}
    >
      <ConvexQueryAuthLifecycle />
      <ConvexQueryCacheProvider>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </ConvexQueryCacheProvider>
    </ConvexBetterAuthProvider>
  );
}
